/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   City Footer Component that shows top 25 US cities
 */

import { Box, type BoxProps, Flex, Heading } from '@chakra-ui/react'
import { TOP_TWENTY_FIVE_USA_CITIES } from '@zcode/nr/es/shared/constants/default_city_groups'
import { MemoAnalyticsTrack } from '@zcode/nr/es/shared/packages/analytics-track'
import { routesConstants } from '@zcode/zumper-routes'
import React from 'react'
import { default as preload } from '../../routes/preload'
import { useAnalyticChannel } from '../AnalyticChannelProvider/useAnalyticChannel'
import { default as PreserveFiltersLinkContainer } from '../PreserveFiltersLink/PreserveFiltersLinkContainer'

const onMouseEnter = () => preload([routesConstants.CURRENT_SEARCH_ROUTE])

interface CityFooterProps extends Omit<BoxProps, 'as'> {
  hideHeader?: boolean
}

const CityFooter = ({ hideHeader, ...boxProps }: CityFooterProps) => {
  const { analyticChannel } = useAnalyticChannel()
  const headerText = "We've got Homes and Apartments for Rent in Popular Cities."

  return (
    <Box<'div'>
      bgColor="transparent"
      p="50px 0"
      minHeight={hideHeader ? '200px' : { base: '580px', desktop: '340px' }}
      w="100%"
      {...boxProps}
    >
      <Box<'div'> w="100%">
        {!hideHeader && (
          <Heading
            as="h2"
            fontSize={{ base: '32px', md: '32px', sm: '2xl' }}
            fontWeight="medium"
            lineHeight={{ base: 8, md: 8, sm: 6 }}
            mb={{ base: 6, md: 8 }}
          >
            {headerText}
          </Heading>
        )}
        <Flex
          align="center"
          direction={{ base: 'row', lg: 'column' }}
          h={{ base: 'initial', lg: '180px' }}
          justify="space-between"
          sx={{
            'a.city-footer-link': {
              '@media (hover: none)': { _hover: { color: '#4f5c68' } },
              _hover: {
                color: '#123c54',
                textDecoration: 'none',
              },
              color: '#00486a',
              flex: { base: '0 0 50%', lg: '0 0 auto' },
              fontSize: 'md',
              h: { base: 10, lg: 'initial' },
              letterSpacing: '0.2px',
              lineHeight: 8,
              overflow: 'hidden',
              paddingRight: '10px',
              paddingTop: '1px',
              textOverflow: 'ellipsis',
              w: { base: 'initial', lg: '20%' },
              whiteSpace: 'nowrap',
            },
          }}
          wrap="wrap"
          w="100%"
        >
          {TOP_TWENTY_FIVE_USA_CITIES.map((city) => (
            <MemoAnalyticsTrack
              action="Footer Link"
              category={analyticChannel}
              key={city.url}
              label={city.name}
              render={({ trackEvent }: { trackEvent: () => void }) => (
                <PreserveFiltersLinkContainer
                  className="city-footer-link"
                  onClick={trackEvent}
                  onMouseEnter={onMouseEnter}
                  to={`/${routesConstants.APTS_BASE_URL}/${city.url}`}
                >
                  {`${city.name} Apartments`}
                </PreserveFiltersLinkContainer>
              )}
              // @ts-expect-error [Property 'renderDeps' does not exist on type]
              renderDeps={[city]}
            />
          ))}
        </Flex>
      </Box>
    </Box>
  )
}

export default CityFooter
